import React from 'react';

import {
  CheckCircleOutlined,
  CopyOutlined,
  PercentageOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Switch } from 'antd';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createEnumOptions,
  getEnumTranslationKey,
  getEnumValue,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { createTableActionsColumn, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { registerModuleFilters } from '@/utils/input/filters';
import { formatCurrency } from '@/utils/output/formatters';

import { AGREEMENT_REVIEW_ACTION, MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

const isTerminalRevision = (record) => {
  if (!record) return false;
  if (record.supersededByAgreementId || record.supersededById) return false;
  if (record.isLatest === false || record.isLatestVersion === false) return false;
  return true;
};

export const formatAgreementVersionLabel = (versionNumber) => {
  if (versionNumber == null || versionNumber === '') return DEFAULT_EMPTY_VALUE;
  return `v${versionNumber}`;
};

export const getChannelAgreementActionFlags = (record) => {
  const approvalStatus = parseOptionalNumericEnumFromApi(
    CoreEnums.ChannelAgreementApprovalStatus,
    record?.approvalStatus,
  );
  const isPendingReview = approvalStatus === CoreEnums.ChannelAgreementApprovalStatus.PendingReview;
  const canReview = isPendingReview;
  const canCreateVersion =
    isTerminalRevision(record) &&
    (approvalStatus === CoreEnums.ChannelAgreementApprovalStatus.Approved ||
      approvalStatus === CoreEnums.ChannelAgreementApprovalStatus.Rejected);

  return {
    canEdit: isPendingReview,
    canDelete: true,
    canDeactivate: true,
    canReview,
    canCreateVersion,
  };
};

const stopRowClick = (e) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();
};

const buildReviewMenuItems = (formatMessage, record, flags, openReview) => {
  if (!flags.canReview) return [];

  return [
    {
      key: 'approve',
      label: formatMessage({ id: 'global.button.approve' }),
      icon: <CheckCircleOutlined />,
      onClick: (e) => {
        stopRowClick(e);
        openReview?.(record.id, AGREEMENT_REVIEW_ACTION.APPROVE);
      },
    },
    {
      key: 'reject',
      label: formatMessage({ id: 'global.button.reject' }),
      icon: <StopOutlined />,
      danger: true,
      onClick: (e) => {
        stopRowClick(e);
        openReview?.(record.id, AGREEMENT_REVIEW_ACTION.REJECT);
      },
    },
    { key: 'divider-review', type: 'divider' },
  ];
};

const buildCreateVersionMenuItems = (formatMessage, record, flags, openCreateVersion) => {
  if (!flags.canCreateVersion) return [];

  return [
    {
      key: 'createVersion',
      label: formatMessage({ id: 'channelAgreement.action.createVersion' }),
      icon: <CopyOutlined />,
      onClick: (e) => {
        stopRowClick(e);
        openCreateVersion?.(record.id);
      },
    },
  ];
};

export const toRegistrationApprovalPolicyApiValue = (value) => {
  const resolved = parseOptionalNumericEnumFromApi(
    CoreEnums.ChannelRegistrationApprovalPolicy,
    value,
  );
  if (resolved === undefined) return undefined;
  const key = getEnumValue(CoreEnums.ChannelRegistrationApprovalPolicy, resolved);
  if (!key) return undefined;
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/_/g, '-')
    .toLowerCase();
};

export const formatAmountWithCurrency = (amount, currency) => {
  const isoCurrency = getEnumValue(CoreEnums.Currency, currency) || 'USD';
  return formatCurrency(amount ?? 0, isoCurrency, {
    maximumFractionDigits: 2,
  });
};

const approvalStatusBadgeConfig = {
  [CoreEnums.ChannelAgreementApprovalStatus.PendingReview]: {
    status: 'processing',
    type: 'process',
  },
  [CoreEnums.ChannelAgreementApprovalStatus.Approved]: {
    status: 'approved',
    type: 'process',
  },
  [CoreEnums.ChannelAgreementApprovalStatus.Rejected]: {
    status: 'rejected',
    type: 'process',
  },
};

const ApprovalStatusFilter = {
  ...BaseFilters.EnumFilter,
  name: 'approvalStatus',
  label: 'channelAgreement.columns.approvalStatus',
  enumNamespace: getEnumNamespace(CoreEnums.ChannelAgreementApprovalStatus, 'Channel.Agreements'),
  enumSource: CoreEnums.ChannelAgreementApprovalStatus,
  options: createEnumOptions(
    CoreEnums.ChannelAgreementApprovalStatus,
    'channelAgreement.approvalStatus',
    { casing: 'camel' },
  ),
};

const AgreementTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'agreementType',
  label: 'channelAgreement.columns.agreementType',
  enumNamespace: getEnumNamespace(CoreEnums.ChannelAgreementType, 'Channel.Agreements'),
  enumSource: CoreEnums.ChannelAgreementType,
  options: createEnumOptions(CoreEnums.ChannelAgreementType, 'channelAgreementType', {
    casing: 'camel',
  }),
};

const OwnerTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'ownerType',
  label: 'channelAgreement.columns.ownerType',
  enumNamespace: getEnumNamespace(CoreEnums.ChannelAgreementOwnerType, 'Channel.Agreements'),
  enumSource: CoreEnums.ChannelAgreementOwnerType,
  options: createEnumOptions(CoreEnums.ChannelAgreementOwnerType, 'channelAgreement.ownerType', {
    casing: 'camel',
  }),
};

const OwnerCompanyFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'ownerCompany',
  label: 'global.text.ownerCompany',
  labelInValue: true,
  displayField: 'commercialName',
  apiRoute: 'company/paged',
  filterSearch: true,
  params: {
    select: 'id,commercialName',
    filter: 'isActive eq true',
    orderBy: 'commercialName asc',
  },
  oDataField: 'ownerCompanyId',
};

const CurrencyFilter = {
  ...BaseFilters.EnumFilter,
  name: 'currency',
  label: 'global.text.currency',
  enumNamespace: getEnumNamespace(CoreEnums.Currency),
  enumSource: CoreEnums.Currency,
  options: createEnumOptions(CoreEnums.Currency, 'currency', {
    casing: 'lower',
  }),
};

const ReviewedAtDateFilter = {
  ...Filters.DateFilter,
  name: 'reviewedAt',
  label: 'channelAgreement.columns.reviewedAt',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  name: 'created',
  label: 'global.created',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const UpdatedDateFilter = {
  ...Filters.DateFilter,
  name: 'updated',
  label: 'global.updated',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

export const filtersConfig = [
  ApprovalStatusFilter,
  AgreementTypeFilter,
  OwnerTypeFilter,
  OwnerCompanyFilter,
  CurrencyFilter,
  Filters.IsActiveFilter,
  ReviewedAtDateFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/**
 * Domain detail mapping: list-matching badges for isActive / approvalStatus / ownerType labels.
 * Other enum selects use `_form.js` locale options.
 */
export function mapChannelAgreementDetailFields(rows, { item }) {
  if (!item || !rows?.length) return rows;

  return rows
    .filter((row) => row.name !== 'rules')
    .map((row) => {
      if (row.name === 'isActive') {
        const isActive = item.isActive === true;
        return {
          ...row,
          type: 'badge',
          badgeType: 'group-item',
          badgeStatus: isActive ? 'success' : 'default',
          value: isActive ? 'global.text.enabled' : 'global.text.disabled',
          locale: true,
        };
      }

      if (row.name === 'ownerType') {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.ChannelAgreementOwnerType,
          item.ownerType,
        );
        if (resolved === undefined) {
          return { ...row, value: null };
        }
        const isGlobal = resolved === CoreEnums.ChannelAgreementOwnerType.Automy;
        return {
          ...row,
          value: isGlobal
            ? 'channelAgreement.ownerType.globalAutomy'
            : 'channelAgreement.ownerType.distributorPrivate',
          locale: true,
        };
      }

      if (row.name === 'approvalStatus') {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.ChannelAgreementApprovalStatus,
          item.approvalStatus,
        );
        const config = resolved !== undefined ? approvalStatusBadgeConfig[resolved] : null;
        return {
          ...row,
          badgeStatus: config?.status || 'default',
          badgeType: config?.type || 'group-item',
        };
      }

      if (row.name === 'registrationApprovalPolicy') {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.ChannelRegistrationApprovalPolicy,
          item.registrationApprovalPolicy,
        );
        if (resolved === undefined) {
          return { ...row, value: null };
        }
        return {
          ...row,
          value: getEnumTranslationKey(
            CoreEnums.ChannelRegistrationApprovalPolicy,
            resolved,
            'channelAgreement.registrationApprovalPolicy',
          ),
          locale: true,
        };
      }

      if (row.name === 'paymentIntervalUnit') {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.PaymentIntervalUnit,
          item.paymentIntervalUnit,
        );
        if (resolved === undefined) {
          return { ...row, value: null };
        }
        return {
          ...row,
          value: getEnumTranslationKey(
            CoreEnums.PaymentIntervalUnit,
            resolved,
            'channelAgreement.paymentIntervalUnit',
          ),
          locale: true,
        };
      }

      if (row.name === 'versionNumber') {
        return {
          ...row,
          value: formatAgreementVersionLabel(item.versionNumber),
        };
      }

      if (row.name === 'agreementSeriesId') {
        const seriesId = item.agreementSeriesId;
        const hasSeries = seriesId != null && seriesId !== '';
        return {
          ...row,
          value: hasSeries ? seriesId : DEFAULT_EMPTY_VALUE,
          copy: hasSeries,
        };
      }

      if (row.name === 'supersedesAgreementId' && !item.supersedesAgreementId) {
        return null;
      }

      return row;
    })
    .filter(Boolean);
}

const renderEnumBadge = (formatMessage, enumObject, value, translationPrefix) => {
  const resolved = parseOptionalNumericEnumFromApi(enumObject, value);
  if (resolved === undefined) {
    return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
  }
  return (
    <Badge
      status="default"
      type="process"
      text={
        formatMessage({
          id: getEnumTranslationKey(enumObject, resolved, translationPrefix),
        }) || DEFAULT_EMPTY_VALUE
      }
    />
  );
};

const renderOwnerTypeBadge = (formatMessage, ownerType) => {
  const resolved = parseOptionalNumericEnumFromApi(CoreEnums.ChannelAgreementOwnerType, ownerType);
  if (resolved === undefined) {
    return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
  }
  const isGlobal = resolved === CoreEnums.ChannelAgreementOwnerType.Automy;
  return (
    <Badge
      status="default"
      type="process"
      text={formatMessage({
        id: isGlobal
          ? 'channelAgreement.ownerType.globalAutomy'
          : 'channelAgreement.ownerType.distributorPrivate',
      })}
    />
  );
};

const renderApprovalStatusBadge = (formatMessage, value) => {
  const resolved = parseOptionalNumericEnumFromApi(CoreEnums.ChannelAgreementApprovalStatus, value);
  if (resolved === undefined) {
    return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
  }
  const config = approvalStatusBadgeConfig[resolved] || {
    status: 'default',
    type: 'group-item',
  };
  return (
    <Badge
      status={config.status}
      type={config.type}
      text={
        formatMessage({
          id: getEnumTranslationKey(
            CoreEnums.ChannelAgreementApprovalStatus,
            resolved,
            'channelAgreement.approvalStatus',
          ),
        }) || DEFAULT_EMPTY_VALUE
      }
    />
  );
};

export function buildChannelAgreementColumns({
  formatMessage,
  openDrawerCreateOrEdit,
  deleteItemConfirm,
  activate,
  activatingItemIds = {},
  searchCriteria,
  isMobileView,
  showRules,
  openReview,
  openCreateVersion,
}) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'isActive',
      title: formatMessage({ id: 'global.text.enabled' }),
      dataIndex: 'isActive',
      align: 'center',
      sorter: true,
      width: 120,
      render: (isActive, item) => {
        const flags = getChannelAgreementActionFlags(item);
        return (
          <Switch
            checked={isActive}
            disabled={!flags.canDeactivate}
            loading={!!activatingItemIds[item?.id]}
            onChange={(active) => activate(item?.id, active)}
          />
        );
      },
    },
    {
      key: 'name',
      title: formatMessage({ id: 'global.name' }),
      dataIndex: 'name',
      sorter: true,
      render: highlight,
    },
    {
      key: 'approvalStatus',
      title: formatMessage({ id: 'channelAgreement.columns.approvalStatus' }),
      dataIndex: 'approvalStatus',
      align: 'center',
      sorter: true,
      render: (value) =>
        value == null ? DEFAULT_EMPTY_VALUE : renderApprovalStatusBadge(formatMessage, value),
    },
    {
      key: 'agreementType',
      title: formatMessage({ id: 'channelAgreement.columns.agreementType' }),
      dataIndex: 'agreementType',
      align: 'center',
      sorter: true,
      render: (value) =>
        renderEnumBadge(
          formatMessage,
          CoreEnums.ChannelAgreementType,
          value,
          'channelAgreementType',
        ),
    },
    {
      key: 'ownerType',
      title: formatMessage({ id: 'channelAgreement.columns.ownerType' }),
      dataIndex: 'ownerType',
      align: 'center',
      sorter: true,
      render: (value) => renderOwnerTypeBadge(formatMessage, value),
    },
    createTableActionsColumn({
      alwaysDropdown: true,
      render: (_, record) => {
        const flags = getChannelAgreementActionFlags(record);
        return (
          <TableRowButtons
            onDetail={() => openDrawerCreateOrEdit(record.id, true, null)}
            onEdit={
              flags.canEdit ? () => openDrawerCreateOrEdit(record.id, false, null) : undefined
            }
            onRemove={flags.canDelete ? () => deleteItemConfirm(record.id) : undefined}
            customItems={[
              ...buildReviewMenuItems(formatMessage, record, flags, openReview),
              ...buildCreateVersionMenuItems(formatMessage, record, flags, openCreateVersion),
              {
                key: 'rules',
                label: formatMessage({ id: 'channelAgreement.rules.title' }),
                icon: <PercentageOutlined />,
                onClick: (e) => {
                  stopRowClick(e);
                  showRules?.(record.id);
                },
              },
              { key: 'divider-1', type: 'divider' },
            ]}
            actionOrder={{
              primary: [],
              secondary: [
                'approve',
                'reject',
                'divider-review',
                'createVersion',
                'rules',
                'divider-1',
                'detail',
                'edit',
                'delete',
              ],
            }}
            formatMessage={formatMessage}
            isMobileView={isMobileView}
            forceDropdown
          />
        );
      },
    }),
  ];
}
